<template>
  <div>
    <!-- Product Details -->
    <section>
      <dl class="description-list grid-x grid-margin-x max-w-sm">
        <dt class="cell small-5" qaid="warranty-claim-line-type-label" v-html="bp('productType')" />
        <dd v-if="isBike" class="cell small-7" qaid="warranty-claim-line-type" v-html="bp('bikes')" />
        <dd v-if="isPart" class="cell small-7" qaid="warranty-claim-line-type" v-html="bp('partsAccessories')" />
        <dt v-if="isBike" class="cell small-5" qaid="warranty-claim-line-serial-label" v-html="bp('serialNumber')" />
        <dd v-if="isBike" class="cell small-7" qaid="warranty-claim-line-serial" v-html="claimLine.serialNumber" />
        <dt
          v-if="isBike"
          class="cell small-5"
          qaid="warranty-claim-line-serial-image-label"
          v-html="bp('serialImage')"
        />
        <dd v-if="isBike" class="cell small-7">
          <div
            v-if="claimLine.serialNumberImage && claimLine.serialNumberImage.url"
            class="file-upload-container has-aspect-ratio"
            style="--aspect-ratio:1"
          >
            <span class="file-uploads file-uploads-html5">
              <img
                qaid="warranty-claim-serial-image"
                :src="claimLine.serialNumberImage ? claimLine.serialNumberImage.url : null"
              />
            </span>
          </div>
          <div class="md:flex flex-wrap mt-25 flex-shrink-0 items-center md:justify-between">
            <div
              class="flex-shrink-0 text-sm text-gray-60"
              qaid="warranty-claim-line-serial-image-name"
              v-html="imageFileName"
            />
          </div>
        </dd>
        <dt class="cell small-5" qaid="warranty-claim-line-qty-label" v-html="bp('qty')" />
        <dd class="cell small-7" qaid="warranty-claim-line-qty" v-html="claimLine.qty" />
        <dt class="cell small-5" qaid="warranty-claim-line-sku-label" v-html="bp('sku')" />
        <dd class="cell small-7" qaid="warranty-claim-line-sku" v-html="claimLine.sku" />
        <dt class="cell small-5" qaid="warranty-claim-line-model-label" v-html="bp('type')" />
        <dd class="cell small-7" qaid="warranty-claim-line-model" v-html="claimLine.modelName" />
        <dt v-if="isBike" class="cell small-5" qaid="warranty-claim-line-year-label" v-html="bp('year')" />
        <dd v-if="isBike" class="cell small-7" qaid="warranty-claim-line-year" v-html="claimLine.modelYear" />
        <dt class="cell small-5" qaid="warranty-claim-line-color-label" v-html="bp('color')" />
        <dd class="cell small-7" qaid="warranty-claim-line-color" v-html="claimLine.color" />
        <dt class="cell small-5" qaid="warranty-claim-line-size-label" v-html="bp('size')" />
        <dd class="cell small-7" qaid="warranty-claim-line-size" v-html="claimLine.size" />
        <dt class="cell small-5" qaid="warranty-claim-line-purchase-label" v-html="bp('purchaseDate')" />
        <dd class="cell small-7" qaid="warranty-claim-line-purchase" v-html="claimLine.purchaseDateDisplay" />
      </dl>
    </section>

    <hr class="rule my-4" />

    <!-- Issue Details -->
    <section>
      <section-header size="sm" class="mb-3">
        <heading :level="4" size="sm" qaid="warranty-claim-issue-details" v-html="bp('issueDetails')" />
      </section-header>
      <dl class="description-list grid-x grid-margin-x max-w-sm">
        <dt class="cell small-5" qaid="warranty-claim-issue-type-label" v-html="bp('issueType')" />
        <dd class="cell small-7" qaid="warranty-claim-issue-type" v-html="claimLine.issueTypeNameSelected" />
        <dt class="cell small-5" qaid="warranty-claim-issue-when-label" v-html="bp('issueWhen')" />
        <dd class="cell small-7" qaid="warranty-claim-issue-when" v-html="damagedOcurredDisplay" />
        <dt class="cell small-5" qaid="warranty-claim-action-label" v-html="bp('issueRequestedAction')" />
        <dd class="cell small-7" qaid="warranty-claim-action" v-html="requestedActionDisplay" />
        <dt class="cell small-5" qaid="warranty-claim-shipment-label" v-html="bp('shipmentOptions')" />
        <dd class="cell small-7" qaid="warranty-claim-shipment-option" v-html="selectedShipmentOption" />
      </dl>
    </section>

    <!-- Attachments -->
    <section v-if="hasAttachments" class="my-6">
      <dl class="description-list grid-x grid-margin-x max-w-sm">
        <dt class="cell small-5 heading text-lg" qaid="warranty-claim-attachments" v-html="bp('attachments')" />
        <dd class="cell small-7">
          <div v-for="(attachment, attachmentIndex) in attachments" :key="attachmentIndex" class="mb-2">
            <div v-if="attachment.src" class="file-upload-container has-aspect-ratio" style="--aspect-ratio:1">
              <span class="file-uploads file-uploads-html5">
                <template v-if="isImage(attachment.fileName)">
                  <img :src="attachment.src" :qaid="`warranty-claim-attachment-image-${attachmentIndex}`" />
                </template>
                <span v-else>
                  <i class="material-icons md-48" :qaid="`warranty-claim-attachment-image-${attachmentIndex}`"
                    >insert_drive_file</i
                  >
                </span>
              </span>
            </div>
            <div class="md:flex flex-wrap mt-25 flex-shrink-0 items-center md:justify-between">
              <div
                class="flex-shrink-0 text-sm text-gray-60"
                :qaid="`warranty-claim-attachment-image-name-${attachmentIndex}`"
                v-html="attachment.fileName"
              />
            </div>
          </div>
        </dd>
      </dl>
    </section>
  </div>
</template>

<script>
import find from 'lodash/find';
import SectionHeader from '@/components/SectionHeader';

export default {
  components: {SectionHeader},
  props: {
    baseProperties: {
      type: Array,
      required: true,
    },
    claimLine: {
      type: Object,
      required: true,
    },
    claimData: {
      type: Object,
      required: true,
    },
  },

  computed: {
    isBike() {
      return this.claimLine.productType === 'Bikes';
    },

    isPart() {
      return this.claimLine.productType === 'PartsAndAccessories';
    },

    imageFileName() {
      if (this.claimLine.serialNumberImage) {
        return this.claimLine.serialNumberImage.name;
      }
      return undefined;
    },

    damagedOcurredDisplay() {
      const method = find(
        this.claimData.damageOccurredMethods,
        (method) => method.code === this.claimLine.selectedDamagedOccurred
      );

      if (method) {
        return method.name;
      }

      return undefined;
    },

    requestedActionDisplay() {
      const method = find(
        this.claimData.requestedActionMethods,
        (method) => method.code === this.claimLine.selectedRequestedAction
      );

      if (method) {
        return method.name;
      }

      return undefined;
    },

    selectedShipmentOption() {
      if (this.claimLine.warrantyConsumerWaiting) {
        return this.bp('shipNow');
      } else {
        return this.bp('nextScheduled');
      }
    },

    hasAttachments() {
      return this.attachments && this.attachments.length > 0;
    },

    attachments() {
      let attachments = [];

      if (this.claimLine.warrantyDamageImage && this.claimLine.warrantyDamageImage.url) {
        attachments.push({
          src: this.claimLine.warrantyDamageImage.url,
          fileName: this.getImageFileName(this.claimLine.warrantyDamageImage),
        });
      }

      if (this.claimLine.warrantyPurchaseProofImage && this.claimLine.warrantyPurchaseProofImage.url) {
        attachments.push({
          src: this.claimLine.warrantyPurchaseProofImage.url,
          fileName: this.getImageFileName(this.claimLine.warrantyPurchaseProofImage),
        });
      }

      if (this.claimLine.warrantyOptionalAttachments && this.claimLine.warrantyOptionalAttachments.length) {
        this.claimLine.warrantyOptionalAttachments.forEach((attachment) => {
          attachments.push({
            src: attachment.url,
            fileName: this.getImageFileName(attachment),
          });
        });
      }

      return attachments;
    },
  },

  methods: {
    getImageFileName(imagePath) {
      if (imagePath) {
        return imagePath.name;
      }
    },

    isImage(fileName) {
      const imageExtensions = 'gif,jpg,jpeg,png';
      let extension = fileName ? fileName.split('.').pop() : null;
      if (extension) return imageExtensions.includes(extension);
    },
  },
};
</script>
